import { useEffect, useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Building2,
  Search
} from 'lucide-react'
import { useBusinessStore } from '../../core/stores/businessStore'
import { useAuthStore } from '../../core/stores/authStore'
import { useTranslation } from '../../core/hooks/useTranslation'
import { Business } from '../../core/models/business'
import BusinessCard from '../../components/business/BusinessCard'
import DeleteModal from '../../components/ui/DeleteModal'
import AddButton from '../../components/buttons/AddButton'
import FilterButton from '../../components/buttons/FilterButton'
import Modal from '../../components/ui/Modal'
import { Card } from '../../components/ui/Card'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { AppRoute } from '@/core/config/app-routes'
import toast from 'react-hot-toast'
import { i18n } from '../../core/i18n'
import { UserRole } from '@/core/enums'

export default function BusinessManagementPage() {
  const {
    businesses,
    isLoading,
    error,
    fetchBusinessesByRole,
    setCurrentBusiness,
    deleteBusiness
  } = useBusinessStore()
  const { user } = useAuthStore()
  const { t } = useTranslation()
  const navigate = useNavigate()

  const [deletingBusiness, setDeletingBusiness] = useState<Business | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [showFilterModal, setShowFilterModal] = useState(false)

  // Applied filters
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('active')

  // Temporary filters for modal (not applied until "Apply" is clicked)
  const [tempStatusFilter, setTempStatusFilter] = useState<'all' | 'active' | 'inactive'>('active')

  // Ref to prevent duplicate loading
  const businessesLoadedRef = useRef(false)
  const isLoadingRef = useRef(false)

  // Load businesses with filters
  const loadBusinesses = async (customFilters?: { status?: 'all' | 'active' | 'inactive' }) => {
    // Prevent duplicate requests
    if (isLoadingRef.current || !user?.userRole) return

    isLoadingRef.current = true
    try {
      const filters = {
        status: customFilters?.status ?? statusFilter
      }

      await fetchBusinessesByRole(
        user.userRole,
        user.businessId,
        filters
      )
    } finally {
      isLoadingRef.current = false
    }
  }

  // Load businesses on mount (only once)
  useEffect(() => {
    if (businessesLoadedRef.current) return

    businessesLoadedRef.current = true
    loadBusinesses()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Copy current filters to temporary filters when modal opens
  useEffect(() => {
    if (showFilterModal) {
      setTempStatusFilter(statusFilter)
    }
  }, [showFilterModal])

  // Businesses are already filtered by backend
  const filteredBusinesses = businesses


  const handleDeleteClick = (business: Business) => {
    setDeletingBusiness(business)
  }

  const handleDeleteConfirm = async () => {
    if (!deletingBusiness) return

    setIsDeleting(true)
    try {
      const success = await deleteBusiness(deletingBusiness.id)
      if (success) {
        toast.success(i18n.translate('businessDeleted'))
        setDeletingBusiness(null)
        // Reload businesses with current filters
        await loadBusinesses()
      }
    } catch (error) {
      console.error('Failed to delete business:', error)
    } finally {
      setIsDeleting(false)
    }
  }

  const handleSelectBusiness = (business: Business) => {
    setCurrentBusiness(business)
    navigate(AppRoute.BUSINESS_OWNER_BUSINESSES_ROUTE + '/' + business.id)
  }

  // Handle apply filters button
  const handleApplyFilters = async () => {
    setStatusFilter(tempStatusFilter)
    setShowFilterModal(false)

    // Reload businesses with new filters
    await loadBusinesses({ status: tempStatusFilter })
  }

  // Get active filters as badges
  const getActiveFilters = () => {
    const filters: Array<{ id: string, label: string, type: string }> = []

    if (statusFilter !== 'all') {
      filters.push({
        id: 'status',
        label: statusFilter === 'active' ? t('active') : t('inactive'),
        type: 'status'
      })
    }

    return filters
  }

  // Remove filter by type
  const removeFilter = async (_filterId: string, type: string) => {
    if (type === 'status') {
      setStatusFilter('all')
      await loadBusinesses({ status: 'all' })
    }
  }


  if (isLoading && businesses.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  const activeFilters = getActiveFilters()

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-2">
          {t('businessManagementDescription')}
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {/* Active Filters Badges */}
          {activeFilters.length > 0 && false && (
            <div className="flex items-center gap-2 flex-wrap">
              {activeFilters.map((filter) => (
                <div
                  key={filter.id}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-orange-100 text-orange-700 rounded-lg"
                >
                  <span>{filter.label}</span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      removeFilter(filter.id, filter.type)
                    }}
                    className="hover:bg-orange-200 rounded-full p-0.5 transition-colors"
                    title={t('remove')}
                    type="button"
                  >
                    <XMarkIcon className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {false && (
            <FilterButton
              onClick={() => setShowFilterModal(true)}
              title={t('filter')}
            />
          )}

          {businesses.length === 0 && user?.userRole === UserRole.BUSINESS_OWNER && (
            <AddButton
              onClick={() => navigate(AppRoute.BUSINESS_OWNER_BUSINESSES_ROUTE + '/create')}
              title={t('addNewBusiness')}
              variant="icon-only"
            />
          )}
        </div>
      </div>

      {/* Businesses Grid */}
      <div className='pb-2'>
        {businesses.length === 0 ? (
          <Card className="overflow-hidden">
            <div className="flex flex-col items-center justify-center py-5">
              <Building2 className="w-16 h-16 text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">{t('noBusinesses')}</h3>
              <p className="text-gray-600 text-center mb-6 max-w-md">
                {t('noBusinessesDescription')}
              </p>
            </div>
          </Card>
        ) : filteredBusinesses.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-5">
            <Search className="w-16 h-16 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">{t('noResults')}</h3>
            <p className="text-gray-600 text-center">
              {t('noResultsDescription')}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredBusinesses.map((business) => (
              <BusinessCard
                key={business.id}
                business={business}
                onSelect={handleSelectBusiness}
              />
            ))}
          </div>
        )}
      </div>

      {/* Filter Modal */}
      <Modal
        isOpen={showFilterModal}
        onClose={() => setShowFilterModal(false)}
        title={t('filter')}
        showCancelButton={true}
        cancelButtonText={t('close')}
        actionButtonText={t('apply')}
        onAction={handleApplyFilters}
      >
        <div className="max-h-[70vh] overflow-y-auto space-y-4">
          {/* Status Filter */}
          <div className="pt-2 pb-2">
            <div className="flex items-center justify-between py-2">
              <span className="text-sm text-gray-700">{t('status')}</span>
              <div className="flex gap-1">
                <button
                  onClick={() => setTempStatusFilter('all')}
                  className={`px-3 py-1 text-xs rounded border ${tempStatusFilter === 'all'
                    ? 'border-orange-500 text-orange-600 bg-orange-50'
                    : 'border-gray-300 text-gray-600 hover:border-gray-400'
                    }`}
                >
                  {t('all')}
                </button>
                <button
                  onClick={() => setTempStatusFilter('active')}
                  className={`px-3 py-1 text-xs rounded border ${tempStatusFilter === 'active'
                    ? 'border-orange-500 text-orange-600 bg-orange-50'
                    : 'border-gray-300 text-gray-600 hover:border-gray-400'
                    }`}
                >
                  {t('active')}
                </button>
                <button
                  onClick={() => setTempStatusFilter('inactive')}
                  className={`px-3 py-1 text-xs rounded border ${tempStatusFilter === 'inactive'
                    ? 'border-orange-500 text-orange-600 bg-orange-50'
                    : 'border-gray-300 text-gray-600 hover:border-gray-400'
                    }`}
                >
                  {t('inactive')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </Modal>

      {/* Modals */}
      {deletingBusiness && (
        <DeleteModal
          isOpen={!!deletingBusiness}
          onClose={() => setDeletingBusiness(null)}
          onConfirm={handleDeleteConfirm}
          title={t('deleteBusiness')}
          message={t('deleteBusinessMessage').replace('{name}', deletingBusiness.name) + '\n\n' + t('deleteBusinessDetails')}
          isLoading={isDeleting}
        />
      )}
    </div>
  )
}